import React from "react";
import { motion as m } from "framer-motion";
import EditorialCover from "./EditorialCover";
import { Spotlight, Tag } from "./ui";

const ProjectCard = ({ project, onOpen, index = 0, size = "default" }) => {
  const { title, category, year, summary, images = [], accent, stack = [] } =
    project;
  const cover = images[0];
  const tall = size === "feature";

  return (
    <m.article
      layout
      initial={{ opacity: 0, y: 32 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{
        duration: 0.7,
        delay: Math.min(index, 5) * 0.06,
        ease: [0.16, 1, 0.3, 1],
      }}
      className="group cursor-pointer"
      onClick={() => onOpen && onOpen(project)}
      onKeyDown={(e) => {
        if ((e.key === "Enter" || e.key === " ") && onOpen) {
          e.preventDefault();
          onOpen(project);
        }
      }}
      role="button"
      tabIndex={0}
      aria-label={`Open case study: ${title}`}
    >
      <Spotlight
        radius={420}
        intensity={0.18}
        className={`relative overflow-hidden rounded-lg bg-paper-warm shadow-card ring-1 ring-[var(--color-border)] transition-[box-shadow,transform] duration-500 ease-standard group-hover:-translate-y-1.5 group-hover:shadow-card-hover dark:bg-night-surface ${
          tall ? "aspect-[4/3]" : "aspect-[5/4]"
        }`}
      >
        {cover ? (
          <img
            src={cover}
            alt={title}
            loading="lazy"
            className="h-full w-full object-cover transition-transform duration-[900ms] ease-standard group-hover:scale-[1.06]"
          />
        ) : (
          <div className="h-full w-full transition-transform duration-[900ms] ease-standard group-hover:scale-[1.04]">
            <EditorialCover title={title} kicker={category} accent={accent} />
          </div>
        )}

        <div className="absolute inset-0 z-10 flex items-end bg-gradient-to-t from-azure-dark/95 via-azure-dark/80 to-azure-dark/40 p-6 opacity-0 transition-opacity duration-500 ease-standard group-hover:opacity-100">
          <div className="translate-y-3 transition-transform duration-500 ease-standard group-hover:translate-y-0">
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-electric-soft">
              View case study
            </p>
            <p className="mt-2 max-w-sm text-sm leading-relaxed text-paper/90">
              {summary}
            </p>
          </div>
        </div>

        <span className="tnum absolute right-4 top-4 z-10 rounded-full bg-paper/90 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.16em] text-azure shadow-sm ring-1 ring-inset ring-[var(--color-border)] backdrop-blur-md dark:bg-night/80 dark:text-electric-soft">
          {year}
        </span>
      </Spotlight>

      <div className="mt-5 flex items-start justify-between gap-6">
        <div>
          <h3 className="font-display text-2xl leading-tight transition-colors duration-300 group-hover:text-azure dark:group-hover:text-electric md:text-[1.75rem]">
            {title}
          </h3>
          <p className="t-eyebrow mt-2">{category}</p>
        </div>
        <svg
          width="22"
          height="22"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          className="mt-2 shrink-0 text-ink-muted transition-all duration-300 group-hover:-translate-y-1 group-hover:translate-x-1 group-hover:text-azure dark:group-hover:text-electric"
          aria-hidden="true"
        >
          <line x1="7" y1="17" x2="17" y2="7" />
          <polyline points="7 7 17 7 17 17" />
        </svg>
      </div>

      {tall && stack.length > 0 && (
        <div className="mt-4 flex flex-wrap gap-2">
          {stack.slice(0, 4).map((s) => (
            <Tag key={s}>{s}</Tag>
          ))}
        </div>
      )}
    </m.article>
  );
};

export default ProjectCard;
